import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendar,
  MatDatepickerIntl,
  MatDatepickerModule,
} from "@angular/material/datepicker";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import {
  FECHA_ESTANDAR_FORMATS,
  FechaEstandarAdapter,
} from "../../utilerias/fecha-estandar.adapter";

import swal from "sweetalert2";

import { VacacionesService } from "../vacaciones.service";
import {
  DiaCalendario,
  notaVisible,
  Saldo,
  Vacacion,
} from "../../shared/model/vacaciones/vacacion.model";
import { generaFrc019 } from "../frc019";
import { agrupaPedidos, tramosDe } from "../pedidos-vacaciones";
import { DIAS_HABILES, semanasHabiles } from "../semanas-habiles";
import { FirmaFrcComponent } from "../firma-frc/firma-frc.component";
import { FrcFirmadoComponent } from "../frc-firmado/frc-firmado.component";
import { confirmaAutorizar, pideMotivo } from "../confirma-respuesta";
import { PermisosComponent } from "../../permisos/permisos.component";
import { PermisosService } from "../../permisos/permisos.service";
import { PermisoEnEquipo, permisoEnEquipo } from "../../permisos/permiso-en-equipo";
import { EtiquetasCalendario, RangoVacaciones } from "./rango-vacaciones";
import {
  agrupaEnTramos,
  CuentaDelPedido,
  CuentaDePeriodo,
  cuentaDelPedido,
  diasHabilesDe,
  MAX_TRAMOS,
  ReglasDeSaldo,
  Tramo,
} from "./tramos-vacaciones";
import { renglonesQueCaben, seDesborda } from "../renglones-que-caben";
import {
  acumuladoUsado,
  acumuladoVista,
  fuentesDelPedido,
  gozadosEnCos,
  gozadosVista,
  paraElSiguiente,
  quedanVista,
  restantesVista,
} from "../saldo-vista";

/** Los pasos del asistente de «Pedir vacaciones». «firma» dura lo que
 *  está abierta la ventana de la firma; «listo», ya enviada. */
type PasoPedir = "fechas" | "revisa" | "firma" | "listo";

/** Cómo se eligen las fechas: un tramo (primer y último día) o días
 *  sueltos, uno por toque. */
type ModoFechas = "seguidos" | "sueltos";

/** El filtro de «Tus vacaciones»: una familia de estado, o TODAS cuando no
 *  hay ninguno prendido. */
type FiltroMias = "TODAS" | "PENDIENTE" | "ACTIVO" | "RECHAZADA";

/** Una celda del calendario «Tu equipo»: sus vacaciones y, desde el
 *  29/10/2026, los permisos de su gente de ese día; lo que no cabe se
 *  cuenta en `mas` y se lista en `masTip`. */
interface DiaDelEquipo extends DiaCalendario {
  permisos: PermisoEnEquipo[];
  mas: number;
  masTip: string;
}

/** Los renglones de un día de «Tu equipo»: con dos, el mes cabe sin rodar. */
const RENGLONES_POR_DIA = 2;

const MESES_CORTOS = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];
const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const DIAS_SEMANA = [
  "domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado",
];

/**
 * Pantalla del colaborador: pedir vacaciones y ver cómo van.
 *
 * A propósito NO enseña el calendario de todos ni deja elegir de quién
 * son los días: aquí sólo se piden los propios. Quién es el que pide lo
 * decide el servidor por la sesión, no esta pantalla.
 */
@Component({
  selector: "app-solicitud-vacaciones",
  standalone: true,
  templateUrl: "./solicitud-vacaciones.component.html",
  // Las piezas del asistente, compartidas con Permisos, y luego lo propio.
  styleUrls: ["../asistente-comun.css", "./solicitud-vacaciones.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatTooltipModule,
    MatDatepickerModule,
    FirmaFrcComponent,
    FrcFirmadoComponent,
    PermisosComponent,
  ],
  providers: [
    // El calendario en español. Va el adaptador junto con el locale: el
    // adaptador toma el idioma al crearse, así que registrar solo el
    // locale no habría cambiado nada. Se reusa el FechaEstandarAdapter
    // de COS, el mismo de la pestaña Permisos.
    { provide: MAT_DATE_LOCALE, useValue: "es-MX" },
    { provide: DateAdapter, useClass: FechaEstandarAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FECHA_ESTANDAR_FORMATS },
    // Primer toque, primer día; segundo, último (ver RangoVacaciones).
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: RangoVacaciones },
    // Lo que el lector de pantalla dice del tramo que se va marcando.
    { provide: MatDatepickerIntl, useClass: EtiquetasCalendario },
  ],
})
export class SolicitudVacacionesComponent implements OnInit {
  /**
   * Qué pestaña se ve. Vacaciones al entrar: es lo que la mayoría viene
   * a hacer, y el permiso es lo excepcional.
   *
   * «equipo» sólo existe para quien autoriza (o a quien hoy le toca
   * resolver algo por suplencia): lo que espera su respuesta —vacaciones
   * y permisos— y el calendario de su gente. Estaban arriba y abajo de las
   * otras dos pestañas y a los líderes les hacían rodar la pantalla
   * (30/09/2026, Norman: «déjalo súper acomodado»); así Vacaciones y
   * Permisos quedan iguales para todos: lo propio.
   */
  pestana: "vacaciones" | "permisos" | "equipo" = "vacaciones";

  /** Cuántos permisos esperan la respuesta de quien entra: la otra mitad
   *  de la cuenta de la pestaña «Mi equipo». */
  permisosPendientes = 0;

  /**
   * Esos permisos, para la bandeja de «Mi equipo». Desde el 29/10/2026 los
   * dibuja esta pantalla con el mismo renglón de las vacaciones (Norman: «no
   * en una ventana sino que salga como el de vacaciones»); antes los
   * dibujaba app-permisos en su propia caja.
   */
  permisosPorResolver: PermisoEnEquipo[] = [];

  /** Los permisos de su gente, pendientes y autorizados, para el calendario
   *  «Tu equipo» (29/10/2026: «también permisos aparezcan en el calendario»). */
  permisosEquipo: PermisoEnEquipo[] = [];

  /** Qué se ve en «Esperan tu respuesta»: vacaciones o permisos. */
  vistaResolver: "vacaciones" | "permisos" = "vacaciones";

  /**
   * El año en curso, fijo. Sólo le dice al servidor desde cuándo mandar
   * «Tus vacaciones»: las que terminan del 1 de enero en adelante, con
   * las que ya pidió para el año que entra.
   *
   * YA NO es la llave del saldo. Desde el 24/09/2026 el saldo va por
   * periodo de aniversario (igual que la hoja de RH), y los días se
   * descuentan del periodo que contiene el primer día que se pide
   * (saldoDeFecha), no del año. No hay navegador de años a propósito:
   * consultar otros años es cosa de Calendario → Seguimiento.
   */
  readonly anio = new Date().getFullYear();

  mias: Vacacion[] = [];

  /**
   * Sus solicitudes como se ven en «Tus vacaciones»: los tramos de un mismo
   * pedido de días sueltos, juntos (15/10/2026). `mias` se queda tramo por
   * tramo: con eso se marcan los días ya pedidos.
   */
  misSolicitudes: Vacacion[] = [];

  /**
   * Sus periodos, como los manda el servidor: el vigente primero y luego
   * el que sigue. Cada uno trae ya sus cuentas (restantes, acumulado y lo
   * que le queda). Aquí no se calcula ningún periodo: la regla del
   * aniversario y la del 29 de febrero viven en el servidor.
   */
  saldos: Saldo[] = [];

  /** El periodo vigente (el que contiene hoy): el de la tarjeta de arriba. */
  saldo: Saldo | null = null;

  /** El periodo que empieza cuando termina el vigente, si ya existe. */
  siguiente: Saldo | null = null;

  /**
   * El último día que se puede pedir (ver ultimoDiaPedible). Es propiedad
   * y no getter por lo mismo que `hoy`: un Date nuevo en cada ciclo
   * reabre el calendario solo.
   *
   * Es el [maxDate] del calendario y además lo revisa avisoFechas: hasta
   * el 30/09/2026 las fechas también se tecleaban, y Material las escribía
   * en el modelo aunque pasaran del tope (sólo las marcaba en rojo). El
   * servidor NO lo cubre: una fecha sin periodo se guardaba sin
   * descontarse de nada.
   */
  maximo: Date | null = null;

  /** Lo que este usuario tiene que resolver. Vacío para quien no
   *  autoriza a nadie, y entonces esa parte ni se dibuja. */
  porResolver: Vacacion[] = [];

  /** Los periodos de su gente, para decidir viendo quién más va a estar
   *  fuera esos días. */
  equipo: Vacacion[] = [];

  resolviendo: number | null = null;

  /** Días festivos del año, en ISO. Los manda el servidor para que la
   *  cuenta de aquí sea la misma que la de allá. */
  festivos = new Set<string>();

  /**
   * Hoy, a las cero horas.
   *
   * Es el mínimo de los calendarios: no tiene sentido pedir vacaciones
   * para la semana pasada. Se guarda en una propiedad y no se calcula
   * en la plantilla, porque un Date nuevo en cada ciclo de detección de
   * cambios reabre el calendario solo.
   */
  readonly hoy = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );

  /**
   * Marca los días del calendario para poder pintarlos.
   *
   * Se declara como propiedad flecha porque [dateClass] guarda la
   * referencia y un método normal perdería el this.
   */
  claseDia = (d: Date): string => {
    if (!d) {
      return "";
    }
    const iso = this.iso(d);
    const clases: string[] = [];
    if (this.festivos.has(iso)) {
      clases.push("es-festivo");
    } else if (this.finDeSemana(d)) {
      clases.push("es-finde");
    }
    // Los días sueltos no son un tramo, así que Material no los pinta
    // como elegidos: van con su clase (ver refrescaDias).
    if (this.sueltos.has(iso)) {
      clases.push("es-suelto");
    }
    if (this.ocupados.has(iso)) {
      clases.push("es-mia");
    }
    return clases.join(" ");
  };

  /** Mes que se está viendo en el calendario del equipo. */
  mesEquipo = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  readonly encabezadosCal = DIAS_HABILES;
  /** La última rejilla armada y con qué se armó (ver semanasEquipo). */
  private rejillaEquipo: {
    mes: number; equipo: Vacacion[]; permisos: PermisoEnEquipo[]; hoy: string; semanas: DiaDelEquipo[][];
  } | null = null;

  cargando = false;
  enviando = false;

  /**
   * Si ya llegó la primera respuesta del servidor. Mientras no, el
   * asistente dice «Consultando…» y el saldo no se dibuja. Al recargar
   * (tras pedir, retirar o resolver) ya no se esconde nada: antes el
   * saldo desaparecía y volvía, y la pantalla brincaba.
   */
  yaCargo = false;

  /** El primer y el último día, como se marcan en el calendario. */
  desde: Date | null = null;
  hasta: Date | null = null;

  /**
   * Las dos puntas juntas, que es lo que pinta el calendario. Se guarda
   * y no se arma en un getter: un DateRange nuevo en cada ciclo de
   * detección de cambios le cambiaría la entrada al calendario sin parar.
   */
  rango = new DateRange<Date>(null, null);

  /* ---------- Días seguidos o días sueltos ----------
     13/10/2026 (Norman: «que también se puedan pedir independientes,
     ejemplo el 15 y otro día el 20, solo esos dos; que tenga las 2
     opciones»). «Días seguidos» es lo de siempre: primer y último día.
     Con «Días sueltos» cada toque pone o quita un día.

     El servidor no cambia: una solicitud es un tramo con sus dos fechas y
     su FRC-019. Lo elegido se junta en tramos (los días pegados van
     juntos) y se manda una solicitud por tramo, todas con la misma firma;
     quien autoriza las resuelve una por una. */

  modo: ModoFechas = "seguidos";

  readonly MODOS: { id: ModoFechas; texto: string; icono: string; ayuda: string }[] = [
    { id: "seguidos", texto: "Días seguidos", icono: "date_range",
      ayuda: "Tocas el primer día y el último" },
    { id: "sueltos", texto: "Días sueltos", icono: "event",
      ayuda: "Tocas cada día que quieres, por ejemplo el 15 y el 20" },
  ];

  /** Los días sueltos elegidos, en ISO. */
  sueltos = new Set<string>();

  /**
   * Lo que se va a pedir, en tramos: uno con «Días seguidos» (cuando ya
   * están las dos puntas) y los que salgan con «Días sueltos». Se arma al
   * elegir (recuenta) y no en un getter: la plantilla lo recorre.
   */
  tramos: Tramo[] = [];

  /** La cuenta de esos tramos contra sus periodos (ver cuentaDelPedido): con ella se valida. */
  cuenta: CuentaDelPedido<Saldo> = { periodos: [], excede: null, negativo: null };

  /**
   * De qué periodo salen los días de lo elegido y cuánto le quedaría a cada
   * uno, con «lo que le sobra primero» (20/10/2026, ver ../saldo-vista): lo
   * que dicen «Revisa» y la cuenta de abajo del calendario. Lo que se pide
   * para después del aniversario sale primero de lo que le sobra al vigente.
   */
  fuentes: CuentaDePeriodo<Saldo>[] = [];

  /**
   * Las fichas de «Revisa» que salen de esa cuenta: por cada periodo, de
   * cuál se descuenta y (`quedan`) cuánto le quedaría. Se arman con la
   * cuenta para que la plantilla las recorra en una sola lista.
   */
  fichasRevisa: { cuenta: CuentaDePeriodo<Saldo>; quedan: boolean; conPeriodo?: boolean }[] = [];

  /** Quiso agregar un día que haría más tramos de los que se mandan de una vez. */
  topeTramos = false;
  readonly MAX_TRAMOS = MAX_TRAMOS;

  /**
   * Los días que ya tiene pedidos o registrados (en espera o autorizados),
   * en ISO. El calendario los marca y no deja elegirlos: el servidor
   * rechaza lo que se encima (VAL-004) después de la firma, y con varios
   * tramos unos entrarían y otros no.
   */
  ocupados = new Set<string>();

  /** Si alguno de esos días es de hoy en adelante: sólo entonces la
   *  leyenda explica el verde (los de antes ya no salen en el calendario). */
  hayPedidosAdelante = false;

  /** Algún tramo pasa por encima de un día ya pedido. */
  private pisaLoPedido = false;

  /* ---------- Dos meses a la vista ----------
     13/10/2026 (Norman: «que al lado otro mes; así tenemos dos meses para
     ver y pedir: el que estamos y uno adelante»). Son dos calendarios de
     Material, uno junto al otro, que se mueven juntos: el de la derecha
     enseña siempre el mes que sigue al de la izquierda. Las flechas y los
     nombres de los meses los pone esta pantalla. */

  /** El mes de la izquierda y el de la derecha (su día 1). Propiedades y
   *  no getters por lo mismo que `hoy`: son el [startAt] de cada calendario. */
  mesIzq = new Date(this.hoy.getFullYear(), this.hoy.getMonth(), 1);
  mesDer = new Date(this.hoy.getFullYear(), this.hoy.getMonth() + 1, 1);

  /**
   * El tramo que quedaría si el último día fuera el que está bajo el
   * puntero. Lo avisa RangoVacaciones y se pinta en los dos meses (como
   * «comparación» de Material): cada calendario sólo sabría dibujarlo en
   * el suyo, y de octubre a noviembre se vería cortado.
   */
  previaDesde: Date | null = null;
  previaHasta: Date | null = null;

  /* ---------- El asistente ----------
     30/09/2026 (Norman: «como wizard ... evitar tanto espacio en
     blanco»): pedir va en tres pasos —fechas, revisa, firma y envía— con
     el calendario a la vista en vez de dos campos. La revisión sustituye
     al «¿Pedir estas vacaciones?» de antes y la firma sigue en su
     ventana (app-firma-frc). Las cuentas y las reglas no cambian: son
     las mismas de siempre (puedeEnviar, seExcede, avisoFechas…). */

  paso: PasoPedir = "fechas";

  readonly PASOS: { id: PasoPedir; texto: string }[] = [
    { id: "fechas", texto: "Fechas" },
    { id: "revisa", texto: "Revisa" },
    { id: "firma", texto: "Firma y envía" },
  ];

  /** Lo que se acaba de enviar, para el aviso del paso «listo». */
  enviada: {
    rango: string;
    dias: number;
    papel: boolean;
    autoriza: string;
  } | null = null;

  /** Las que se acaban de pedir, para resaltarlas en «Tus vacaciones». */
  recienPedidas: number[] = [];

  /**
   * Los próximos festivos (hasta cuatro), de hoy en adelante: los mismos
   * que el calendario pinta de fucsia, en una lista junto a él. Cada uno
   * con su hojita (mes y día), el día de la semana, cuánto falta y si hace
   * fin de semana largo (cae en lunes o viernes), que es lo que se busca al
   * planear vacaciones. Se arman al cargar y no en un getter, por lo mismo
   * que `rango`: son fechas nuevas cada vez.
   */
  proximosFestivos: {
    fecha: Date;
    texto: string;
    mes: string;
    dia: number;
    semana: string;
    anio: string;
    falta: string;
    largo: boolean;
  }[] = [];

  /** Los dos calendarios del paso «Fechas», mientras se ven. Al aparecer
   *  cada uno se le sigue el mes (ver sigueA). */
  private calIzq?: MatCalendar<Date>;
  private calDer?: MatCalendar<Date>;

  @ViewChild("calIzq") set calendarioIzq(c: MatCalendar<Date> | undefined) {
    if (c !== this.calIzq) {
      this.calIzq = c;
      this.enlaza(c, 0);
    }
  }

  @ViewChild("calDer") set calendarioDer(c: MatCalendar<Date> | undefined) {
    if (c !== this.calDer) {
      this.calDer = c;
      this.enlaza(c, 1);
    }
  }

  /** La caja del mes de la derecha: en angosto no se ve (sólo cabe uno). */
  @ViewChild("calDer", { read: ElementRef }) private cajaDer?: ElementRef<HTMLElement>;

  /**
   * El calendario no deja elegir sábado ni domingo: pedirlos no tendría
   * sentido y además descuadraría la cuenta de días hábiles.
   *
   * Se declara como propiedad flecha y no como método porque el
   * [dateFilter] del calendario guarda la referencia: un método normal
   * perdería el this.
   */
  soloHabiles = (d: Date | null): boolean => {
    if (!d) {
      return false;
    }
    const dia = d.getDay();
    if (dia === 0 || dia === 6) {
      return false;
    }
    // Tampoco un festivo: empezar o terminar las vacaciones en un día
    // que de todas formas se descansa no tiene sentido, y ademas no
    // descontaria nada.
    return !this.festivos.has(this.iso(d));
  };

  /** Lo que el calendario deja tocar: día hábil y que no tenga ya pedido. */
  sePuedePedir = (d: Date | null): boolean => {
    return this.soloHabiles(d) && !!d && !this.ocupados.has(this.iso(d));
  };

  /** Las reglas del saldo para la cuenta de lo pedido: de qué periodo se
   *  descuenta cada tramo, cuánto le queda y a cuál se le acumula. */
  private readonly reglasSaldo: ReglasDeSaldo<Saldo> = {
    periodoDe: (d) => this.saldoDeFecha(this.iso(d)),
    quedan: (s) => this.quedanDe(s),
    siguiente: (s) => {
      const sig = this.siguienteDe(s);
      return sig && sig.arrastreCalculado !== false ? sig : null;
    },
  };

  /* La cuenta de lo que espera respuesta (vacaciones y permisos) va en la
     pestaña «Mi equipo», que es donde se responde; Vacaciones y Permisos
     son de quien PIDE y no la llevan (30/09/2026). */

  constructor(
    private service: VacacionesService,
    private permisosService: PermisosService,
    private cdr: ChangeDetectorRef,
    // La misma instancia que usa el calendario para el punteado: así el
    // toque y lo que se dibuja siguen una sola regla.
    @Inject(MAT_DATE_RANGE_SELECTION_STRATEGY)
    private reglaRango: RangoVacaciones
  ) {
    this.reglaRango.alPrevisualizar = (tramo) => this.ponPrevia(tramo.start, tramo.end);
  }

  ngOnInit(): void {
    this.cargar();
    this.cargaPermisosPorResolver();
  }

  /* ================= «Mi equipo» ================= */

  /** Quien ve la pestaña «Mi equipo»: quien autoriza a alguien, o quien
   *  hoy tiene algo que resolver (suplencias). */
  get esLider(): boolean {
    return this.esAutorizador || this.porResolver.length > 0 || this.permisosPendientes > 0;
  }

  /** La cuenta de la pestaña: vacaciones y permisos que esperan. */
  get pendientesEquipo(): number {
    return this.porResolver.length + this.permisosPendientes;
  }

  /** Los permisos que esperan su respuesta: la cuenta de la pestaña y,
   *  desde el 29/10/2026, los renglones de su bandeja. */
  private cargaPermisosPorResolver(): void {
    this.permisosService.porResolver().subscribe({
      next: (res) => {
        if (!res.codigoError) {
          this.permisosPorResolver = (res.porResolver || []).map((p) => permisoEnEquipo(p, this.anio));
          this.permisosPendientes = this.permisosPorResolver.length;
          this.cdr.detectChanges();
          this.ajustaPaginaResolver();
        }
      },
      error: () => {
        // Sin la cuenta de permisos la pestaña sigue con las vacaciones.
      },
    });
  }

  /**
   * Los permisos de su gente para el calendario «Tu equipo»: del 1 de enero
   * en adelante, como sus vacaciones (29/10/2026). Si no llegan, el
   * calendario sigue con las vacaciones.
   */
  private cargaPermisosEquipo(): void {
    this.permisosService.equipo(`${this.anio}-01-01`).subscribe({
      next: (res) => {
        if (!res.codigoError) {
          this.permisosEquipo = (res.permisos || []).map((p) => permisoEnEquipo(p, this.anio));
          this.cdr.detectChanges();
        }
      },
      error: () => {
        // Sin permisos el calendario no se queda en blanco: siguen sus vacaciones.
      },
    });
  }

  /** La pestaña de Permisos recargó y su cuenta es otra: algo se pidió o se
   *  resolvió en otro lado, así que la bandeja se vuelve a traer. */
  alCambiarPermisos(n: number): void {
    if (n !== this.permisosPendientes) {
      this.cargaPermisosPorResolver();
    }
  }

  /** Se resolvió un permiso de la bandeja: sale de ella y cambia en el
   *  calendario (autorizado, o fuera si se negó). */
  alResolverPermiso(): void {
    this.cargaPermisosPorResolver();
    this.cargaPermisosEquipo();
  }

  /** Abre «Mi equipo» en lo que tenga pendientes: vacaciones primero. */
  aEquipo(): void {
    this.pestana = "equipo";
    this.vistaResolver =
      this.porResolver.length || !this.permisosPendientes ? "vacaciones" : "permisos";
    this.cdr.detectChanges();
    this.ajustaPaginaResolver();
    // El calendario sólo lo ve quien tiene equipo propio.
    if (this.esAutorizador) {
      this.cargaPermisosEquipo();
    }
  }

  eligeVistaResolver(v: "vacaciones" | "permisos"): void {
    if (v !== this.vistaResolver) {
      // Cada bandeja empieza en su primera página.
      this.paginaResolver = 1;
    }
    this.vistaResolver = v;
    this.cdr.detectChanges();
    this.ajustaPaginaResolver();
  }

  /* Lo que espera respuesta, por páginas de las que caben en su columna
     (como «Tus vacaciones»). Desde el 29/10/2026 el mismo paginado para las
     dos bandejas: el de la que se está viendo. */
  paginaResolver = 1;
  porPaginaResolver = 4;

  @ViewChild("listaResolver") private listaResolver?: ElementRef<HTMLElement>;

  /** Cuántas hay en la bandeja que se está viendo. */
  private get enEspera(): number {
    return this.vistaResolver === "vacaciones" ? this.porResolver.length : this.permisosPorResolver.length;
  }

  get totalPaginasResolver(): number {
    return Math.max(1, Math.ceil(this.enEspera / this.porPaginaResolver));
  }

  get paginaResolverNorm(): number {
    return Math.min(Math.max(this.paginaResolver, 1), this.totalPaginasResolver);
  }

  get porResolverPagina(): Vacacion[] {
    const inicio = (this.paginaResolverNorm - 1) * this.porPaginaResolver;
    return this.porResolver.slice(inicio, inicio + this.porPaginaResolver);
  }

  /** Los permisos de la página que se ve. */
  get permisosPagina(): PermisoEnEquipo[] {
    const inicio = (this.paginaResolverNorm - 1) * this.porPaginaResolver;
    return this.permisosPorResolver.slice(inicio, inicio + this.porPaginaResolver);
  }

  /** El paginado del encabezado: el de la bandeja que se está viendo. */
  get paginasEspera(): { actual: number; total: number } {
    return { actual: this.paginaResolverNorm, total: this.totalPaginasResolver };
  }

  mueveEspera(delta: number): void {
    this.paginaResolver = Math.min(
      Math.max(this.paginaResolverNorm + delta, 1),
      this.totalPaginasResolver
    );
  }

  trackPermiso(_: number, f: PermisoEnEquipo): number | undefined {
    return f.permiso.idPermiso;
  }

  /** Se estima con una solicitud de 112 px (nombre, fechas y sus botones)
   *  y, ya pintadas, una menos si no caben. */
  private ajustaPaginaResolver(): void {
    const n = renglonesQueCaben(this.listaResolver?.nativeElement, 112, { maximo: 8, enColumna: 5 });
    if (n === null) {
      return;
    }
    if (n !== this.porPaginaResolver) {
      this.porPaginaResolver = n;
      this.cdr.detectChanges();
    }
    while (this.porPaginaResolver > 2 && seDesborda(this.listaResolver?.nativeElement)) {
      this.porPaginaResolver--;
      this.cdr.detectChanges();
    }
  }

  /* ================= Datos ================= */

  cargar(): void {
    this.cargando = true;
    this.service.mias(this.anio).subscribe({
      next: (res) => {
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo consultar.", "error");
        } else {
          this.mias = res.vacaciones || [];
          this.misSolicitudes = agrupaPedidos(this.mias);
          this.saldos = res.saldos || [];
          // El vigente lo marca el servidor; si no viniera marcado, el
          // primero, que es como los ordena.
          this.saldo =
            this.saldos.find((s) => s.vigente === true) || this.saldos[0] || null;
          this.siguiente = this.siguienteDe(this.saldo);
          this.maximo = this.ultimoDiaPedible();
          this.porResolver = agrupaPedidos(res.pendientes || []);
          this.equipo = res.equipo || [];
          this.esAutorizador = res.puedeAutorizar === true;
          // Si ya está en «Mi equipo» (entró antes de que esto llegara), su
          // calendario también trae los permisos (29/10/2026).
          if (this.pestana === "equipo" && this.esAutorizador) {
            this.cargaPermisosEquipo();
          }
          this.festivos = new Set<string>(res.festivos || []);
          this.ocupados = this.diasOcupados(this.mias);
          const desdeHoy = this.iso(this.hoy);
          this.hayPedidosAdelante = [...this.ocupados].some((d) => d >= desdeHoy);
          // Con el saldo y lo ocupado al día, lo que estuviera elegido se
          // vuelve a contar.
          this.recuenta();
          const hoy = this.iso(this.hoy);
          this.proximosFestivos = [...this.festivos]
            .filter((f) => f >= hoy)
            .sort((a, b) => Number(a > b) - Number(a < b))
            .slice(0, 4)
            .map((f) => {
              const fecha = this.aFecha(f);
              const semana = DIAS_SEMANA[fecha.getDay()];
              return {
                fecha,
                texto: this.diaLargo(fecha),
                mes: MESES_CORTOS[fecha.getMonth()],
                dia: fecha.getDate(),
                semana: semana.charAt(0).toUpperCase() + semana.slice(1),
                anio: this.anioSiOtro(fecha),
                falta: this.enCuanto(fecha),
                largo: fecha.getDay() === 1 || fecha.getDay() === 5,
              };
            });
          // Si el filtro se quedó sin ninguna (retiró la única en espera),
          // se apaga en vez de dejar la lista vacía.
          if (!this.cuantasMias(this.filtroMias)) {
            this.filtroMias = "TODAS";
          }
        }
        this.cargando = false;
        this.yaCargo = true;
        this.cdr.detectChanges();
        // Ya pintada la columna derecha se sabe dónde empieza la lista. Se
        // vuelve a medir cuando llegan las fuentes: sin ellas los íconos
        // son texto y las tarjetas miden de más.
        this.ajustaPaginaMias();
        this.ajustaPaginaResolver();
        this.muestraLaNueva();
        // Lo que se pidió o se retiró cambia los días marcados.
        this.refrescaDias();
        void document.fonts?.ready.then(() => {
          this.ajustaPaginaMias();
          this.ajustaPaginaResolver();
        });
      },
      error: () => {
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cargando = false;
        this.yaCargo = true;
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Saldo ================= */

  /*
   * Desde el 24/09/2026 el saldo es un PERIODO de aniversario —del
   * aniversario de su Alta IMSS al siguiente—, igual que la hoja de RH:
   *
   *   días de goce − gozados = restantes;  restantes + acumulado = le quedan
   *
   * Lo que sobra de un periodo pasa solo al siguiente como acumulado. Las
   * cuentas las manda hechas el servidor (restantes y disponibles); aquí
   * sólo se enseñan. Quien no tiene fecha de ingreso sigue con el año
   * calendario como periodo.
   */

  /**
   * El periodo del que se descuenta una vacación que empieza en `fecha`
   * (ISO): el que la contiene, del inicio (incluido) al fin (excluido: el
   * fin ya es el aniversario siguiente). Las fechas ISO se pueden comparar
   * como texto.
   *
   * Si ninguno la contiene, null y NO el vigente: una fecha tecleada fuera
   * de sus periodos (un 15/03/2029) hacía decir «De tu periodo del
   * 20/09/2026 al 20/09/2027 te quedarían…», que es falso. Esas fechas las
   * detiene avisoFechas.
   */
  saldoDeFecha(fecha: string): Saldo | null {
    const s = this.saldos.find(
      (x) =>
        !!x.periodoInicio &&
        !!x.periodoFin &&
        x.periodoInicio <= fecha &&
        fecha < x.periodoFin
    );
    return s || null;
  }

  /**
   * El periodo al que irían las fechas que está eligiendo: el que contiene
   * el primer día. Una vacación se descuenta COMPLETA del periodo en que
   * empieza, aunque termine en el siguiente (así lo cuenta el servidor).
   */
  get saldoPedido(): Saldo | null {
    const primero = this.tramos.length ? this.tramos[0].desde : this.desde;
    return primero ? this.saldoDeFecha(this.iso(primero)) : this.saldo;
  }

  /** Días de goce del periodo vigente. null mientras no se los asignen. */
  get diasTotales(): number | null {
    return this.saldo && this.saldo.diasTotales != null ? this.saldo.diasTotales : null;
  }

  /** Gozados del periodo vigente, con «lo que le sobra primero» (../saldo-vista). */
  get diasUsados(): number {
    return this.saldo ? gozadosVista(this.saldo) : 0;
  }

  /** Días de goce − gozados del vigente: la columna «Días restantes». */
  get restantes(): number | null {
    return this.saldo ? restantesVista(this.saldo) : null;
  }

  /** Lo que le queda del vigente: restantes + acumulado. */
  get disponibles(): number | null {
    return this.saldo ? quedanVista(this.saldo) : null;
  }

  /**
   * Lo acumulado de periodos anteriores en el vigente: lo fijó RH o salió
   * solo de lo que le sobró del anterior. Puede ser negativo si tomó de
   * más antes. 0 si no hay nada.
   */
  get diasArrastre(): number {
    return (this.saldo && acumuladoVista(this.saldo)) || 0;
  }

  /** El acumulado con su signo, porque en la cuenta de arriba va sumado. */
  get acumuladoConSigno(): string {
    const n = this.diasArrastre;
    return n < 0 ? `−${Math.abs(n)}` : `+${n}`;
  }

  /**
   * GOZADOS = lo que tiene en COS dentro del periodo (autorizado o en
   * espera, ver desgloseUsados) + lo que ya había gozado fuera de COS.
   *
   * Lo segundo lo trae el Excel de RH y no tiene fechas: es un bulto
   * cuadrado al día de la carga. La misma suma se hace en el
   * calendario y en el seguimiento; si aquí faltara, esta pantalla le
   * diría al colaborador que le quedan días que RH ya le descontó.
   */
  private usadosDe(s: Saldo | null): number {
    return s ? (s.diasTomados || 0) + (s.diasGozadosPrevios || 0) : 0;
  }

  /**
   * Días de goce − gozados. Lo manda el servidor; la resta de aquí sólo
   * responde si viniera sin él, para no enseñar un hueco. null mientras no
   * le asignen días.
   */
  private restantesDe(s: Saldo | null): number | null {
    if (!s || s.diasTotales == null) {
      return null;
    }
    return s.restantes != null ? s.restantes : s.diasTotales - this.usadosDe(s);
  }

  /**
   * Lo que de verdad puede pedir de ese periodo: restantes + acumulado. Lo
   * manda el servidor (disponibles), con el mismo respaldo que restantesDe.
   * El acumulado se puede tomar, pero no suma a la prima (art. 80), que se
   * paga sobre los días de goce del periodo.
   */
  private quedanDe(s: Saldo | null): number | null {
    const restantes = this.restantesDe(s);
    if (restantes === null || !s) {
      return null;
    }
    return s.disponibles != null ? s.disponibles : restantes + (s.diasArrastre || 0);
  }

  /**
   * De dónde salen sus días de goce, para el tooltip. Sólo se dice «los
   * de la ley» cuando lo son: si RH los ajustó a mano, no se le atribuyen
   * a la ley.
   */
  get tipGoce(): string {
    const s = this.saldo;
    if (s && s.porAniversario && s.antiguedad != null &&
        s.diasPorLey != null && s.diasTotales === s.diasPorLey) {
      if (s.antiguedad < 1) {
        return "Todavía no cumples tu primer año: la ley da vacaciones a partir de él.";
      }
      const anios = s.antiguedad === 1 ? "1 año cumplido" : `${s.antiguedad} años cumplidos`;
      return `Los que te da la ley con ${anios} al empezar tu periodo.`;
    }
    return "Los días que te asignaron para este periodo.";
  }

  /**
   * "N en COS + M de antes", para el tooltip de gozados.
   *
   * Lo de COS (diasTomados) cuenta lo autorizado Y lo que espera respuesta,
   * con fechas pasadas o futuras, y también lo que capturó quien administra
   * el calendario: todo eso aparta días. Por eso no se dice «que pediste»
   * ni «que ya gozaste»: quien acaba de pedir diciembre no lo ha gozado, y
   * lo capturado por RH no lo pidió él.
   */
  get desgloseUsados(): string {
    const s = this.saldo;
    const enCos = s ? gozadosEnCos(s) : 0;
    const previos = s?.diasGozadosPrevios || 0;
    const delSiguiente = s ? paraElSiguiente(s) : 0;
    const dias = enCos === 1 ? "día" : "días";
    const base = previos
      ? `${enCos} en COS (autorizados o en espera) + ${previos} que RH tiene registrados de antes`
      : `${enCos} ${dias} en COS (autorizados o en espera)`;
    // «Lo que le sobra primero»: lo pedido para su siguiente periodo sale de aquí.
    if (!delSiguiente) {
      return base;
    }
    const cuantos = delSiguiente === enCos ? `Los ${delSiguiente}` : `De esos, ${delSiguiente}`;
    return `${base}. ${cuantos} son de lo que pediste para tu siguiente periodo: se toman primero de lo que te sobra aquí.`;
  }

  /** Qué es el acumulado y de dónde salió, para su tooltip. */
  get tipAcumulado(): string {
    const n = this.diasArrastre;
    if (n < 0) {
      const dias = Math.abs(n) === 1 ? "día" : "días";
      return `Tomaste ${Math.abs(n)} ${dias} de más en periodos anteriores: se descuentan de este.`;
    }
    const usado = this.saldo ? acumuladoUsado(this.saldo) : 0;
    if (!n) {
      return usado
        ? `Lo que te sobró de antes (${usado}) ya se usó: lo que te sobra se usa primero.`
        : "No traes días acumulados de periodos anteriores.";
    }
    const origen = this.saldo?.arrastreCalculado === false
      ? "Lo que RH tiene registrado que te sobró de periodos anteriores."
      : "Lo que te sobró de tu periodo anterior.";
    const yaUsado = usado ? ` Ya se usaron ${usado}: lo que te sobra se usa primero.` : "";
    return `${origen}${yaUsado} Ya está sumado en lo que te queda.`;
  }

  /** "N de este periodo + M de acumulado", para el tooltip de lo que queda. */
  get desgloseQuedan(): string {
    const restantes = this.restantes;
    if (restantes === null) {
      return "";
    }
    const acumulado = this.diasArrastre;
    if (!acumulado) {
      return `${restantes} ${restantes === 1 ? "día" : "días"} de este periodo`;
    }
    const signo = acumulado < 0 ? "−" : "+";
    return `${restantes} de este periodo ${signo} ${Math.abs(acumulado)} de acumulado`;
  }

  /**
   * Si hay que aclararle que su periodo es el año calendario: sólo mientras
   * RH no captura su fecha de ingreso. Se pide `false` y no «falsy»: si el
   * servidor no mandara el dato, no se afirma algo que no se sabe.
   */
  get periodoCalendario(): boolean {
    return !!this.saldo && this.saldo.porAniversario === false;
  }

  /** "Tu siguiente periodo empieza el …", o vacío si todavía no existe. */
  get notaSiguiente(): string {
    const s = this.siguiente;
    if (!s || !s.periodoInicio) {
      return "";
    }
    const cuando = `Tu siguiente periodo empieza el ${this.corta(s.periodoInicio)}`;
    if (s.diasTotales == null) {
      return `${cuando}.`;
    }
    const dias = s.diasTotales === 1 ? "día" : "días";
    return `${cuando}: ${s.diasTotales} ${dias} de goce + lo que te sobre de este.`;
  }

  /** Días hábiles que abarca lo que está eligiendo, entre todos sus tramos. */
  get diasPedidos(): number {
    return this.tramos.reduce((n, t) => n + t.dias, 0);
  }

  /**
   * El periodo al que no le alcanza para lo pedido, con su cuenta: lo que
   * dice el aviso rojo. No siempre es el de la tarjeta de arriba: quien
   * pide para después de su aniversario pide contra su periodo siguiente,
   * que ya trae sumado como acumulado lo que le sobre del vigente.
   */
  get excedido(): CuentaDePeriodo<Saldo> | null {
    return this.cuenta.excede;
  }

  /** De dónde salen los días de lo pedido, con días asignados: de cada uno
   *  se dice cuánto le quedaría (ver fuentes). */
  get cuentasConSaldo(): CuentaDePeriodo<Saldo>[] {
    return this.fuentes.filter((c) => c.quedarian !== null);
  }

  /**
   * De cuál periodo, en la cuenta de abajo del calendario, cuando hace falta
   * decirlo: « de este periodo» (si son varios), « de tu siguiente periodo»
   * o « de tu periodo del … al …». Lo del vigente solo no lleva nada: es el
   * «te quedan» de la derecha. Así son todos una sola frase.
   */
  etiquetaFuente(c: CuentaDePeriodo<Saldo>): string {
    if (c.periodo === this.saldo) {
      return this.cuentasConSaldo.length > 1 ? " de este periodo" : "";
    }
    if (c.periodo === this.siguiente) {
      return " de tu siguiente periodo";
    }
    return ` de tu periodo ${this.textoPeriodo(c.periodo)}`;
  }

  /**
   * Ese periodo no es el de las fechas: sus días salen de lo que le sobra
   * (Irving pide desde el 21/12/2026 y salen de su periodo 2025-2026).
   * «Revisa» lo dice en la ficha, para que «del 18/12/2025…» con fechas de
   * 2027 no desconcierte.
   */
  esDeLoQueSobra(c: CuentaDePeriodo<Saldo>): boolean {
    return !this.cuenta.periodos.some((p) => p.periodo === c.periodo);
  }

  /** La etiqueta de una ficha de «Revisa». */
  etiquetaFicha(f: { cuenta: CuentaDePeriodo<Saldo>; quedan: boolean }): string {
    if (f.quedan) {
      return "Te quedarían";
    }
    return this.esDeLoQueSobra(f.cuenta) ? "De lo que te sobra" : "De tu periodo";
  }

  /** El texto chico de una ficha con varios periodos: de cuál es. */
  notaFicha(c: CuentaDePeriodo<Saldo>): string {
    const periodo = this.textoPeriodo(c.periodo);
    return this.esDeLoQueSobra(c) ? `lo que te sobra ${periodo}` : periodo;
  }

  /** Qué parte de lo que le queda a ese periodo se llevan estas fechas, en
   *  %, para la barra de «Revisa». */
  parteDe(c: CuentaDePeriodo<Saldo>): number {
    if (c.tiene === null || c.tiene <= 0) {
      return 100;
    }
    return Math.min(100, Math.round((c.pide / c.tiene) * 100));
  }

  /**
   * No se puede pedir por saldo: o no le alcanza en el periodo en que
   * empiezan las fechas, o le alcanza pero dejaría en negativo un periodo
   * posterior (ver siguienteEnNegativo).
   */
  get seExcede(): boolean {
    return this.excedePeriodo || !!this.siguienteEnNegativo;
  }

  /** Pide más de lo que le queda en el periodo en que empiezan las fechas
   *  (con días sueltos, en alguno de sus periodos). */
  get excedePeriodo(): boolean {
    return !!this.cuenta.excede;
  }

  /**
   * El periodo posterior que quedaría en negativo con lo que está pidiendo,
   * o null.
   *
   * Lo que sobra de un periodo pasa solo al siguiente como acumulado, así
   * que pedir días del vigente le quita esos mismos días al siguiente. Si
   * en el siguiente ya pidió de más contando con ese acumulado (le quedan
   * 10, el siguiente trae 12 de goce y ya pidió 15 de él), pedir 8 del
   * vigente le alcanza aquí («te quedarían 2») pero deja al siguiente en
   * −1. El servidor lo rechaza (VAL-006) después de la firma; hay que
   * decirlo antes.
   *
   * La cadena sigue mientras el acumulado sea el calculado: si RH fijó el
   * del siguiente, lo que pase antes ya no le llega. Y se corta en un
   * periodo sin días de goce, que al siguiente le pasa 0 (así cuenta el
   * servidor).
   */
  get siguienteEnNegativo(): Saldo | null {
    return this.cuenta.negativo ? this.cuenta.negativo.periodo : null;
  }

  /** En cuánto quedaría ese periodo posterior, con su signo. */
  get quedariaSiguiente(): string {
    if (!this.cuenta.negativo) {
      return "";
    }
    const n = this.cuenta.negativo.quedaria;
    return n < 0 ? `−${Math.abs(n)}` : `${n}`;
  }

  /**
   * Por qué las fechas no se pueden pedir, o vacío si sí se puede.
   *
   * El calendario no deja elegir nada de esto; la revisión se queda de
   * red. Hasta el 30/09/2026 las fechas también se tecleaban (dd/MM/aaaa)
   * y Material las escribía en el modelo aunque quedaran antes de [min],
   * después de [max] o fuera del filtro: sin esta revisión se podía pedir
   * un 15/03/2029, que no cae en ningún periodo, y el servidor la guardaba
   * sin descontarla de ninguno.
   */
  get avisoFechas(): string {
    // Las puntas de cada tramo (con días sueltos, cada punta es un día
    // elegido) o, mientras falta el último día, el primero.
    const puntas = this.tramos.length
      ? this.tramos.flatMap((t) => [t.desde, t.hasta])
      : [this.desde].filter((d): d is Date => !!d);
    const tope = this.maximo;
    if (puntas.some((d) => d < this.hoy)) {
      return "No se pueden pedir días que ya pasaron.";
    }
    if (tope && puntas.some((d) => d > tope)) {
      return `Por ahora puedes pedir hasta el ${this.corta(this.iso(tope))}.`;
    }
    if (puntas.some((d) => !this.soloHabiles(d))) {
      return "Tus vacaciones tienen que empezar y terminar en día hábil: ni fin de semana ni día festivo.";
    }
    if (this.pisaLoPedido) {
      return "Dentro de esas fechas ya tienes vacaciones pedidas o registradas.";
    }
    return "";
  }

  /**
   * Las fechas pasan al periodo siguiente (cruzan su aniversario). No se
   * reparten: se descuentan completas del periodo en que empiezan, y hay
   * que decírselo para que no espere verlas partidas.
   */
  get cruzaPeriodo(): boolean {
    // Sólo le pasa a un tramo de días seguidos: los sueltos no se juntan
    // de un periodo a otro (ver tramosSueltos).
    const t = this.unico;
    const s = this.saldoPedido;
    return !!t && !!s && !!s.periodoFin && this.iso(t.hasta) >= s.periodoFin;
  }

  /**
   * «del 20/09/2026 al 20/09/2027», como las columnas «Inicio» y «Fin del
   * periodo» de la hoja de RH: el fin es el aniversario siguiente.
   */
  textoPeriodo(s: Saldo | null): string {
    if (!s || !s.periodoInicio || !s.periodoFin) {
      return "";
    }
    return `del ${this.corta(s.periodoInicio)} al ${this.corta(s.periodoFin)}`;
  }

  get puedeEnviar(): boolean {
    // avisoFechas ya cubre fin de semana y festivo en las puntas, además
    // de lo que queda antes de hoy o después del último día pedible.
    return (
      !this.enviando &&
      this.tramos.length > 0 &&
      this.diasPedidos > 0 &&
      !this.seExcede &&
      !this.avisoFechas
    );
  }

  /* ================= Pedir: el asistente ================= */

  /** El número del paso en que va (0 a 2), o 3 cuando ya se envió. */
  get indicePaso(): number {
    const i = this.PASOS.findIndex((p) => p.id === this.paso);
    return i < 0 ? this.PASOS.length : i;
  }

  /**
   * A qué paso se puede volver tocándolo arriba: sólo a «Fechas» desde
   * «Revisa». Con la firma abierta o enviando, a ninguno; ya enviada, se
   * empieza de nuevo con «Pedir otras fechas».
   */
  puedeIrA(i: number): boolean {
    return this.paso === "revisa" && i === 0;
  }

  irAPaso(i: number): void {
    if (this.puedeIrA(i)) {
      this.paso = this.PASOS[i].id;
    }
  }

  /**
   * Un toque en cualquiera de los dos calendarios. Con «Días seguidos»:
   * primer día, último día o volver a empezar (ver RangoVacaciones). Con
   * «Días sueltos»: pone el día o lo quita.
   */
  eligeDia(fecha: Date | null, calendario?: MatCalendar<Date>): void {
    if (this.modo === "sueltos") {
      this.alternaSuelto(fecha, calendario);
      return;
    }
    this.rango = this.reglaRango.selectionFinished(fecha, this.rango);
    this.desde = this.rango.start;
    this.hasta = this.rango.end;
    this.ponPrevia(null, null);
    this.recuenta();
  }

  /** Pone o quita un día suelto. Si con él salieran más tramos de los que
   *  se mandan de una vez, no entra y se avisa al pie. */
  private alternaSuelto(fecha: Date | null, calendario?: MatCalendar<Date>): void {
    if (!fecha) {
      return;
    }
    const iso = this.iso(fecha);
    if (this.sueltos.has(iso)) {
      this.sueltos.delete(iso);
      this.topeTramos = false;
    } else {
      this.sueltos.add(iso);
      this.topeTramos = this.tramosSueltos().length > MAX_TRAMOS;
      if (this.topeTramos) {
        this.sueltos.delete(iso);
      }
    }
    this.recuenta();
    this.refrescaDias(calendario);
  }

  /** La × de un tramo en «Días elegidos»: quita sus días. */
  quitaTramo(t: Tramo): void {
    for (const d of diasHabilesDe(t.desde, t.hasta, this.soloHabiles)) {
      this.sueltos.delete(this.iso(d));
    }
    this.topeTramos = false;
    this.recuenta();
    this.refrescaDias();
  }

  /**
   * De «Días seguidos» a «Días sueltos» y al revés, sin perder lo elegido
   * cuando se puede: un tramo pasa a sus días, uno por uno, y los días
   * sueltos que forman un solo tramo pasan a primer y último día. Varios
   * tramos no caben en «Días seguidos»: ahí se empieza de nuevo.
   */
  cambiaModo(modo: ModoFechas): void {
    if (modo === this.modo) {
      return;
    }
    if (modo === "sueltos") {
      const dias = this.desde
        ? diasHabilesDe(this.desde, this.hasta || this.desde, this.sePuedePedir)
        : [];
      this.sueltos = new Set(dias.map((d) => this.iso(d)));
      this.ponRango(null, null);
    } else {
      const t = this.unico;
      this.sueltos = new Set<string>();
      this.ponRango(t ? t.desde : null, t ? t.hasta : null);
    }
    this.modo = modo;
    this.topeTramos = false;
    this.ponPrevia(null, null);
    this.recuenta();
    this.refrescaDias();
  }

  limpiaFechas(): void {
    this.ponRango(null, null);
    this.sueltos = new Set<string>();
    this.topeTramos = false;
    this.ponPrevia(null, null);
    this.recuenta();
    this.refrescaDias();
  }

  private ponRango(desde: Date | null, hasta: Date | null): void {
    this.desde = desde;
    this.hasta = hasta;
    this.rango = new DateRange<Date>(desde, hasta);
  }

  /** El único tramo de lo elegido, o null si no hay o son varios. */
  get unico(): Tramo | null {
    return this.tramos.length === 1 ? this.tramos[0] : null;
  }

  /** Los primeros tramos, que son los que caben en un renglón de «Días
   *  elegidos»; de los demás se dice cuántos son (se ven en el calendario). */
  get tramosALaVista(): Tramo[] {
    return this.tramos.slice(0, 3);
  }

  /**
   * Arma los tramos de lo elegido y su cuenta contra el saldo. Se llama
   * cada vez que cambia lo elegido, el saldo o lo ya pedido.
   */
  private recuenta(): void {
    this.tramos = this.modo === "sueltos" ? this.tramosSueltos() : this.tramoSeguido();
    this.cuenta = cuentaDelPedido(this.tramos, this.reglasSaldo);
    // Los periodos en orden, el vigente primero: la cuenta de «lo que le sobra
    // primero» va del más viejo al más nuevo.
    const enOrden = [...this.saldos].sort((a, b) => (a.periodoInicio || "").localeCompare(b.periodoInicio || ""));
    this.fuentes = fuentesDelPedido(this.tramos, enOrden, (d) => this.saldoDeFecha(this.iso(d)));
    // Con varios periodos, una ficha por periodo (lo que le quedaría, con sus
    // fechas debajo): así caben en un renglón junto a «La autoriza».
    const varios = this.fuentes.length > 1;
    this.fichasRevisa = this.fuentes.flatMap((cuenta) => {
      if (varios && cuenta.quedarian !== null) {
        return [{ cuenta, quedan: true, conPeriodo: true }];
      }
      return [
        ...(this.textoPeriodo(cuenta.periodo) ? [{ cuenta, quedan: false }] : []),
        ...(cuenta.quedarian !== null ? [{ cuenta, quedan: true }] : []),
      ];
    });
    this.pisaLoPedido = this.tramos.some(
      (t) => diasHabilesDe(t.desde, t.hasta, (d) => this.ocupados.has(this.iso(d))).length > 0
    );
  }

  private tramoSeguido(): Tramo[] {
    return this.desde && this.hasta
      ? [{ desde: this.desde, hasta: this.hasta, dias: this.habilesEntre(this.desde, this.hasta) }]
      : [];
  }

  /** Los días sueltos, en tramos: los pegados van juntos, pero no de un
   *  periodo a otro (cada día se descuenta del suyo). */
  private tramosSueltos(): Tramo[] {
    return agrupaEnTramos(
      [...this.sueltos].map((s) => this.aFecha(s)),
      this.soloHabiles,
      (d) => this.saldoDeFecha(this.iso(d))
    );
  }

  /** Cada día de las vacaciones que ya ocupan lugar: en espera o autorizadas. */
  private diasOcupados(mias: Vacacion[]): Set<string> {
    const dias = new Set<string>();
    for (const v of mias) {
      if ((v.estatus === "PENDIENTE" || v.estatus === "ACTIVO") && v.fechaInicio && v.fechaFin) {
        const todos = diasHabilesDe(this.aFecha(v.fechaInicio), this.aFecha(v.fechaFin), () => true);
        todos.forEach((d) => dias.add(this.iso(d)));
      }
    }
    return dias;
  }

  /**
   * Vuelve a pintar los días de los dos calendarios. Los días sueltos y
   * los ya pedidos van con clase (claseDia), y Material sólo la calcula
   * al armar el mes. Con `foco`, el día recién tocado recupera el foco,
   * que se pierde al rearmarse (importa con teclado).
   */
  private refrescaDias(foco?: MatCalendar<Date>): void {
    this.calIzq?.updateTodaysDate();
    this.calDer?.updateTodaysDate();
    foco?.focusActiveCell();
  }

  private ponPrevia(desde: Date | null, hasta: Date | null): void {
    // Sobre el mismo primer día no hay tramo que anunciar.
    const vacia = !desde || !hasta || desde.getTime() === hasta.getTime();
    const d = vacia ? null : desde;
    const h = vacia ? null : hasta;
    if (d?.getTime() === this.previaDesde?.getTime() && h?.getTime() === this.previaHasta?.getTime()) {
      return;
    }
    this.previaDesde = d;
    this.previaHasta = h;
    this.cdr.markForCheck();
  }

  /* ---------- Los dos meses ---------- */

  /** «octubre 2026»: el nombre de cada mes, sobre su calendario. */
  tituloMes(mes: Date): string {
    return `${MESES[mes.getMonth()]} ${mes.getFullYear()}`;
  }

  /** Si se ven los dos meses: en angosto el de la derecha se oculta y el
   *  de la izquierda se recorre de uno en uno. */
  private get seVenDos(): boolean {
    return !!this.cajaDer && this.cajaDer.nativeElement.offsetWidth > 0;
  }

  /** El último mes que puede ir a la izquierda: con dos a la vista, el
   *  anterior al último que se puede pedir (ese va a la derecha). */
  private get topeIzq(): Date {
    const hoy = this.primerDia(this.hoy);
    const max = this.maximo ? this.primerDia(this.maximo) : new Date(this.anio + 1, 11, 1);
    const tope = this.seVenDos ? new Date(max.getFullYear(), max.getMonth() - 1, 1) : max;
    return tope < hoy ? hoy : tope;
  }

  get puedeRetroceder(): boolean {
    return this.mesIzq > this.primerDia(this.hoy);
  }

  get puedeAvanzar(): boolean {
    return this.mesIzq < this.topeIzq;
  }

  /** Las flechas: los dos meses se recorren juntos, de uno en uno. */
  mueveMes(delta: number): void {
    this.ponMes(new Date(this.mesIzq.getFullYear(), this.mesIzq.getMonth() + delta, 1));
  }

  /** «Hoy» y los «Próximos festivos»: trae ese mes a la vista, sin elegir
   *  nada, para ver qué días quedan alrededor. Si ya se ve, no se mueve. */
  verMes(fecha: Date): void {
    const mes = this.primerDia(fecha).getTime();
    if (mes === this.mesIzq.getTime() || (this.seVenDos && mes === this.mesDer.getTime())) {
      return;
    }
    this.ponMes(this.primerDia(fecha));
  }

  /** Deja `mes` a la izquierda (dentro de lo que se puede pedir) y el que
   *  le sigue a la derecha, y lleva ahí los dos calendarios. */
  private ponMes(mes: Date): void {
    const hoy = this.primerDia(this.hoy);
    const tope = this.topeIzq;
    let m = mes < hoy ? hoy : mes;
    if (m > tope) {
      m = tope;
    }
    this.mesIzq = m;
    this.mesDer = new Date(m.getFullYear(), m.getMonth() + 1, 1);
    this.alinea(this.calIzq, this.mesIzq);
    this.alinea(this.calDer, this.mesDer);
    this.cdr.markForCheck();
  }

  private alinea(calendario: MatCalendar<Date> | undefined, mes: Date): void {
    if (calendario && this.primerDia(calendario.activeDate).getTime() !== mes.getTime()) {
      // Material lo ajusta solo a lo que se puede pedir (minDate y maxDate).
      calendario.activeDate = mes;
    }
  }

  /**
   * Cada calendario avisa cuando cambia de mes por su cuenta (con las
   * flechas del teclado se pasa de un mes al otro): el par lo sigue.
   * `lugar` es 0 para el de la izquierda y 1 para el de la derecha.
   */
  private enlaza(calendario: MatCalendar<Date> | undefined, lugar: number): void {
    // stateChanges se completa solo cuando el calendario se destruye.
    calendario?.stateChanges.subscribe(() => this.sigueA(calendario, lugar));
  }

  private sigueA(calendario: MatCalendar<Date>, lugar: number): void {
    // El de la derecha, oculto, no manda: Material lo topa en el último
    // mes y arrastraría al de la izquierda.
    if (lugar === 1 && !this.seVenDos) {
      return;
    }
    const activa = calendario.activeDate;
    const mes = new Date(activa.getFullYear(), activa.getMonth() - lugar, 1);
    if (mes.getTime() !== this.mesIzq.getTime()) {
      this.ponMes(mes);
    }
  }

  private primerDia(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }

  /** De «Fechas» a «Revisa», con lo mismo que antes pedía el botón
   *  «Pedir vacaciones» (puedeEnviar). */
  aRevisar(): void {
    if (this.puedeEnviar) {
      this.paso = "revisa";
    }
  }

  aFechas(): void {
    if (this.paso === "revisa") {
      this.paso = "fechas";
    }
  }

  /**
   * De «Revisa» a la firma, en su ventana de siempre. Si la cierra sin
   * firmar vuelve a «Revisa» (cancelaFirma); si firma, se envía.
   */
  firmarYEnviar(): void {
    if (this.paso !== "revisa" || !this.puedeEnviar) {
      return;
    }
    this.paso = "firma";
    this.abreFirma("pedir");
  }

  /** «Pedir otras fechas», ya enviada la anterior. */
  otraSolicitud(): void {
    this.enviada = null;
    this.recienPedidas = [];
    this.limpiaFechas();
    this.paso = "fechas";
  }

  /**
   * Manda lo elegido como UNA solicitud (15/10/2026). Un tramo va como
   * siempre; varios (días sueltos) van juntos y con la misma firma, y el
   * servidor guarda todos o ninguno: no hay envíos a medias.
   */
  private enviar(firma: string): void {
    this.enviando = true;
    const tramos = this.tramos.slice();
    const fechas = tramos.map((t) => ({ fechaInicio: this.iso(t.desde), fechaFin: this.iso(t.hasta) }));
    const envio =
      fechas.length > 1
        ? this.service.solicitarPedido(fechas, firma)
        : this.service.solicitar({ ...fechas[0], firmaSolicita: firma });
    envio.subscribe({
      next: (res) => {
        if (res.codigoError) {
          this.alFallarEnvio(res.mensajeError || "No se pudo enviar.");
          return;
        }
        let ids = res.ids || [];
        if (!ids.length && res.idGenerado != null) {
          ids = [res.idGenerado];
        }
        this.alEnviarTodo(tramos, ids, firma);
      },
      error: () => this.alFallarEnvio("No se pudo conectar con el servidor."),
    });
  }

  private alEnviarTodo(tramos: Tramo[], ids: number[], firma: string): void {
    this.enviando = false;
    const papel = firma === FirmaFrcComponent.FISICA;
    // El aviso de «listo» se arma antes de limpiar las fechas.
    this.enviada = {
      rango: this.textoTramos(tramos),
      dias: tramos.reduce((n, t) => n + t.dias, 0),
      papel,
      autoriza: this.autorizadorHoy,
    };
    this.recienPedidas = ids;
    // Sin filtro, y al recargar, a la página donde quedó.
    this.filtroMias = "TODAS";
    this.irALaNueva = true;
    this.limpiaFechas();
    this.paso = "listo";
    // Firma en papel: se descarga el FRC-019, con la línea en blanco. Es
    // uno aunque sean varios tramos: va con el id del pedido (el primero).
    if (papel && ids.length) {
      this.descargaFrc(ids[0]);
    }
    // Sin el «quedó registrada» de antes: lo dice el paso «listo», y lo
    // pedido aparece resaltado en «Tus vacaciones».
    this.cargar();
  }

  /**
   * La solicitud no entró: de vuelta a «Revisa» con las fechas como
   * estaban. Con varios tramos no se guardó ninguno, y el aviso del
   * servidor dice cuál tramo no pasó.
   */
  private alFallarEnvio(mensaje: string): void {
    this.enviando = false;
    this.paso = "revisa";
    this.aviso(mensaje, "error");
    this.cdr.detectChanges();
  }

  /** «el jueves 15 de octubre y el martes 20 de octubre»; con más de tres
   *  tramos, cuántos son y entre qué días. */
  private textoTramos(tramos: Tramo[]): string {
    const ultimo = tramos.at(-1);
    if (tramos.length > 3 && ultimo) {
      return `${tramos.length} tramos, del ${this.diaLargo(tramos[0].desde)} al ${this.diaLargo(ultimo.hasta)}`;
    }
    const textos = tramos.map((t) => this.rangoLargo(t.desde, t.hasta));
    return textos.length > 1
      ? `${textos.slice(0, -1).join(", ")} y ${textos.at(-1)}`
      : textos.join("");
  }

  /* ================= Retirar lo propio ================= */

  retirando: number | null = null;

  retirar(v: Vacacion): void {
    if (v.idVacacion == null) {
      return;
    }
    swal
      .fire({
        title: "¿Retirar esta solicitud?",
        html:
          `<div style="text-align:left;line-height:1.7;font-size:15px">` +
          `${this.textoRango(v)}` +
          `<div style="margin-top:10px;padding-top:10px;border-top:1px solid #e6ebf2;` +
          `color:#475569;font-size:13.5px">Los <b>${this.diasDe(v)}</b> ` +
          `${this.diasDe(v) === 1 ? "día vuelve" : "días vuelven"} a tu saldo. ` +
          `Si la necesitas otra vez, tendrás que pedirla de nuevo.</div></div>`,
        icon: "warning",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Sí, retirar",
        cancelButtonText: "Mejor no",
        confirmButtonColor: "#b71c1c",
      })
      .then((r) => {
        if (!r.isConfirmed) {
          return;
        }
        this.retirando = v.idVacacion!;
        this.service.cancelarMia(v.idVacacion!).subscribe({
          next: (res) => {
            this.retirando = null;
            if (res.codigoError) {
              this.aviso(res.mensajeError || "No se pudo retirar.", "error");
              this.cdr.detectChanges();
              return;
            }
            this.aviso("Retirada. Los días vuelven a tu saldo.", "success");
            this.cargar();
          },
          error: () => {
            this.retirando = null;
            this.aviso("No se pudo conectar con el servidor.", "error");
            this.cdr.detectChanges();
          },
        });
      });
  }

  /* ================= Autorizar ================= */

  /**
   * Autoriza a alguien, así que se le dibuja esa parte de la pantalla.
   *
   * Lo dice el servidor consultando el saldo, no se deduce de si hay
   * periodos: un líder cuyo equipo todavía no ha pedido nada sigue
   * siendo autorizador y tiene que ver su sección desde el primer día.
   */
  esAutorizador = false;

  /* Las dos preguntas (autorizar y el motivo de un «no») son las mismas de
     los permisos desde el 29/10/2026 (../confirma-respuesta): así la del
     permiso se ve como ésta. */

  autorizar(v: Vacacion): void {
    if (v.idVacacion == null) {
      return;
    }
    const dias = this.diasDe(v);
    void confirmaAutorizar({
      titulo: "¿Autorizar estas vacaciones?",
      nombre: v.colaborador,
      detalle: this.textoRango(v),
      nota: `Son <b>${dias}</b> ${dias === 1 ? "día hábil" : "días hábiles"}.`,
      documento: "FRC-019",
    }).then((r) => {
      if (!r) {
        return;
      }
      // Una solicitud pedida antes de la firma electrónica no tiene
      // documento que sellar: se autoriza sin firmar.
      if (v.tieneFirma) {
        this.abreFirma("autorizar", v);
      } else {
        this.resuelve(v, true);
      }
    });
  }

  rechazar(v: Vacacion): void {
    if (v.idVacacion == null) {
      return;
    }
    void pideMotivo(v.colaborador, this.textoRango(v), "Ej. Esa semana cierra el mes y no hay quien cubra.").then(
      (motivo) => {
        if (motivo !== null) {
          this.resuelve(v, false, motivo);
        }
      }
    );
  }

  private resuelve(v: Vacacion, aprobar: boolean, motivo?: string, firma?: string): void {
    this.resolviendo = v.idVacacion || null;
    this.service.resolver(v.idVacacion!, aprobar, motivo, firma).subscribe({
      next: (res) => {
        this.resolviendo = null;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo resolver.", "error");
          this.cdr.detectChanges();
          return;
        }
        // Firma en papel: se descarga el FRC-019 con la línea en blanco.
        if (aprobar && firma === FirmaFrcComponent.FISICA) {
          this.descargaFrc(v.idVacacion!);
        }
        this.aviso(
          aprobar ? "Autorizada." : "Quedó como no autorizada.",
          "success"
        );
        this.cargar();
      },
      error: () => {
        this.resolviendo = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= La firma y el FRC-019 ================= */

  /**
   * El FRC-019 se firma aquí mismo (app-firma-frc): el colaborador al
   * pedir y el líder al autorizar, digital o descargando el formato
   * para firmarlo en papel. Un rechazo no se firma — su evidencia es
   * el propio rechazo con su motivo, y el documento de esa solicitud
   * se elimina.
   */
  firmaAbierta = false;
  firmaPara: "pedir" | "autorizar" = "pedir";
  firmaObjetivo: Vacacion | null = null;
  generandoFrc: number | null = null;

  abreFirma(para: "pedir" | "autorizar", v?: Vacacion): void {
    this.firmaPara = para;
    this.firmaObjetivo = v || null;
    this.firmaAbierta = true;
    // Se llega desde el .then del swal, fuera del ciclo de Angular:
    // sin esto el modal no se pinta.
    this.cdr.detectChanges();
  }

  cierraFirma(): void {
    this.firmaAbierta = false;
    this.firmaObjetivo = null;
    this.cdr.detectChanges();
  }

  /** La cerró sin firmar: si estaba pidiendo, el asistente vuelve a
   *  «Revisa» con las fechas como estaban. */
  cancelaFirma(): void {
    if (this.firmaPara === "pedir" && this.paso === "firma") {
      this.paso = "revisa";
    }
    this.cierraFirma();
  }

  /** Lo que emite el modal: el PNG trazado, o FISICA si se firmará en papel. */
  firmada(firma: string): void {
    const para = this.firmaPara;
    const objetivo = this.firmaObjetivo;
    this.cierraFirma();
    if (para === "pedir") {
      this.enviar(firma);
    } else if (objetivo) {
      this.resuelve(objetivo, true, undefined, firma);
    }
  }

  /** El documento existe para lo pedido con firma y no negado. */
  tieneFrc(v: Vacacion): boolean {
    return (
      v.origen === "SOLICITUD" &&
      v.tieneFirma === true &&
      (v.estatus === "PENDIENTE" || v.estatus === "ACTIVO")
    );
  }

  frcDe(v: Vacacion): void {
    if (v.idVacacion != null) {
      this.descargaFrc(v.idVacacion);
    }
  }

  /** Pide los datos con firmas y arma el PDF. Con días sueltos el servidor
   *  manda los tramos del pedido y el formato lleva un renglón por tramo. */
  private descargaFrc(idVacacion: number): void {
    this.generandoFrc = idVacacion;
    this.service.frc(idVacacion).subscribe({
      next: (res) => {
        this.generandoFrc = null;
        const dato = (res.vacaciones || [])[0];
        if (res.codigoError || !dato) {
          this.aviso(res.mensajeError || "No se pudo armar el FRC-019.", "error");
          this.cdr.detectChanges();
          return;
        }
        generaFrc019(dato, (t) => this.diasDe(t));
        this.cdr.detectChanges();
      },
      error: () => {
        this.generandoFrc = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Calendario del equipo ================= */

  mueveMesEquipo(delta: number): void {
    this.mesEquipo = new Date(
      this.mesEquipo.getFullYear(),
      this.mesEquipo.getMonth() + delta,
      1
    );
  }

  get tituloMesEquipo(): string {
    const meses = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
    ];
    return `${meses[this.mesEquipo.getMonth()]} ${this.mesEquipo.getFullYear()}`;
  }

  /**
   * Las semanas del mes, de lunes a viernes: el fin de semana no se
   * trabaja y no se pinta.
   *
   * Es un getter, pero la rejilla sólo se rearma si cambió el mes, el
   * equipo o el día: con *ngFor, un arreglo nuevo en cada ciclo de
   * detección repintaría todas las celdas (y cerraría los tooltips) sin
   * parar.
   */
  get semanasEquipo(): DiaDelEquipo[][] {
    const hoy = this.iso(new Date());
    const r = this.rejillaEquipo;
    if (r?.mes === this.mesEquipo.getTime() && r.equipo === this.equipo &&
        r.permisos === this.permisosEquipo && r.hoy === hoy) {
      return r.semanas;
    }
    const semanas = semanasHabiles<DiaDelEquipo>(
      this.mesEquipo.getFullYear(),
      this.mesEquipo.getMonth(),
      (fecha) => {
        const iso = this.iso(fecha);
        return {
          iso,
          numero: fecha.getDate(),
          esFinDeSemana: false,
          esHoy: iso === hoy,
          regresan: [],
          ...this.acomodaDia(
            this.equipo.filter(
              (v) => !!v.fechaInicio && !!v.fechaFin &&
                     iso >= v.fechaInicio && iso <= v.fechaFin
            ),
            // Un permiso es de un solo día (29/10/2026).
            this.permisosEquipo.filter((p) => p.fecha === iso),
          ),
        };
      },
      () => ({
        iso: null, numero: null, esFinDeSemana: false,
        esHoy: false, periodos: [], regresan: [], permisos: [], mas: 0, masTip: "",
      }),
    );
    this.rejillaEquipo = {
      mes: this.mesEquipo.getTime(), equipo: this.equipo, permisos: this.permisosEquipo, hoy, semanas,
    };
    return semanas;
  }

  /** Un color con transparencia, para el relleno de la etiqueta. */
  tinte(color: string | undefined, alpha: number): string {
    const c = color || "#0369a1";
    const n = c.replace("#", "");
    const r = Number.parseInt(n.substring(0, 2), 16);
    const g = Number.parseInt(n.substring(2, 4), 16);
    const b = Number.parseInt(n.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Lo que cabe en un día del calendario «Tu equipo»: dos renglones. Si hay
   * más, el primero y «+N más», con los demás en su globo. 29/10/2026: con
   * los permisos junto a las vacaciones, un día con tres crecía su semana y
   * la página rodaba (Norman: «sin scroll»).
   */
  private acomodaDia(
    periodos: Vacacion[], permisos: PermisoEnEquipo[]
  ): Pick<DiaDelEquipo, "periodos" | "permisos" | "mas" | "masTip"> {
    if (periodos.length + permisos.length <= RENGLONES_POR_DIA) {
      return { periodos, permisos, mas: 0, masTip: "" };
    }
    // Uno a la vista (las vacaciones primero) y el renglón de «+N más».
    const caben = RENGLONES_POR_DIA - 1;
    const vac = periodos.slice(0, caben);
    const per = permisos.slice(0, caben - vac.length);
    const ocultos = [
      ...periodos.slice(vac.length).map((v) => this.tipVacacion(v)),
      ...permisos.slice(per.length).map((f) => f.tip),
    ];
    return { periodos: vac, permisos: per, mas: ocultos.length, masTip: ocultos.join("\n") };
  }

  /** El globo de una vacación en «Tu equipo»: quién, cuándo y si espera. */
  tipVacacion(v: Vacacion): string {
    return `${v.colaborador} · ${this.textoRango(v)}${v.estatus === "PENDIENTE" ? " · esperando tu respuesta" : ""}`;
  }

  /* ================= Presentación ================= */

  /** Quién le resuelve. Viene en su propio saldo. */
  get autorizador(): string {
    return this.saldo && this.saldo.autorizaNombre ? this.saldo.autorizaNombre : "";
  }

  /**
   * Quién le resuelve HOY: el suplente si su autorizador no está, o el
   * de siempre. Es a quien tiene sentido perseguir.
   */
  get autorizadorHoy(): string {
    return this.saldo && this.saldo.suplenteNombre ? this.saldo.suplenteNombre : this.autorizador;
  }

  /** "Irving está de vacaciones hasta el 20/09", o vacío si está. */
  get ausenciaAutorizador(): string {
    if (!this.saldo || !this.saldo.suplenteNombre || !this.saldo.ausencia) {
      return "";
    }
    return `${this.autorizador} está ${this.saldo.ausencia}`;
  }

  /**
   * TODAS sus vacaciones en una sola lista, las que esperan respuesta
   * primero. El servidor ya manda las que terminan del 1 de enero en
   * adelante, incluidas las que pidió para el año que entra; aquí no se
   * filtra ni se agrupa por año: con periodos de aniversario el año ya no
   * dice de qué saldo salen.
   *
   * Antes iban en dos bloques, y el segundo se llamaba "Tus vacaciones
   * de 2026" pero dejaba fuera justo las que acababa de pedir. Un
   * título que promete todo y enseña una parte confunde más que ayudar.
   */
  get misPeriodos(): Vacacion[] {
    const pendiente = (v: Vacacion) => (v.estatus === "PENDIENTE" ? 0 : 1);
    return this.misSolicitudes.slice().sort((a, b) => {
      const d = pendiente(a) - pendiente(b);
      if (d !== 0) {
        return d;
      }
      // Dentro de cada grupo, lo más reciente arriba.
      return (b.fechaInicio || "").localeCompare(a.fechaInicio || "");
    });
  }

  /** Días hábiles de un periodo ya guardado; de un pedido de días sueltos, los de sus tramos. */
  diasDe(v: Vacacion): number {
    return tramosDe(v).reduce(
      (n, t) => (t.fechaInicio && t.fechaFin ? n + this.habilesEntre(this.aFecha(t.fechaInicio), this.aFecha(t.fechaFin)) : n),
      0
    );
  }

  etiqueta(v: Vacacion): string {
    if (v.estatus === "PENDIENTE") {
      // Se nombra a quien tiene que responder HOY: saber a quién
      // preguntar vale más que saber que "está pendiente". Si el líder
      // está fuera, es su suplente, y se dice por qué.
      if (!this.autorizadorHoy) {
        return "Esperando autorización";
      }
      return this.ausenciaAutorizador
        ? `Esperando a ${this.autorizadorHoy} (${this.ausenciaAutorizador})`
        : `Esperando a ${this.autorizadorHoy}`;
    }
    if (v.estatus === "RECHAZADA") {
      return "No autorizada";
    }
    return v.origen === "SOLICITUD" ? "Autorizada" : "Registrada";
  }

  icono(v: Vacacion): string {
    if (v.estatus === "PENDIENTE") {
      return "hourglass_top";
    }
    return v.estatus === "RECHAZADA" ? "cancel" : "check_circle";
  }

  clase(v: Vacacion): string {
    if (v.estatus === "PENDIENTE") {
      return "es-pendiente";
    }
    return v.estatus === "RECHAZADA" ? "es-rechazada" : "es-ok";
  }

  textoRango(v: Vacacion): string {
    // Días sueltos: cada tramo, «2 de noviembre — 4 de noviembre y 23 de noviembre».
    if (v.tramos && v.tramos.length > 1) {
      const textos = v.tramos.map((t) => this.textoRango(t));
      return `${textos.slice(0, -1).join(", ")} y ${textos.at(-1)}`;
    }
    if (!v.fechaInicio || !v.fechaFin) {
      return "";
    }
    if (v.fechaInicio === v.fechaFin) {
      return this.bonita(v.fechaInicio);
    }
    // Si las dos puntas son del mismo año, el año (cuando va) sale una
    // sola vez, al final: «5 de enero — 9 de enero de 2027».
    const mismoAnio = v.fechaInicio.substring(0, 4) === v.fechaFin.substring(0, 4);
    return `${this.bonita(v.fechaInicio, !mismoAnio)} — ${this.bonita(v.fechaFin)}`;
  }

  /**
   * «5 de enero», y «5 de enero de 2027» cuando no es del año en curso.
   *
   * «Tus vacaciones» junta las de enero de este año, ya pasadas, con las
   * que pidió para el que entra, y el título ya no dice el año: sin él, dos
   * tarjetas «5 de enero — 9 de enero» de 2026 y de 2027 se veían iguales,
   * y lo mismo en «¿Retirar esta solicitud?».
   */
  bonita(iso?: string, conAnio = true): string {
    if (!iso) {
      return "";
    }
    const d = this.aFecha(iso);
    const anio = conAnio && d.getFullYear() !== this.anio ? ` de ${d.getFullYear()}` : "";
    return `${d.getDate()} de ${MESES[d.getMonth()]}${anio}`;
  }

  /* ================= Las fechas del asistente ================= */

  /** «lun 5 oct», y «lun 4 ene 2027» si no es de este año: las cajas de
   *  «Primer día» y «Último día». */
  diaCorto(d: Date | null): string {
    if (!d) {
      return "";
    }
    const anio = d.getFullYear() !== this.anio ? ` ${d.getFullYear()}` : "";
    return `${DIAS_SEMANA[d.getDay()].substring(0, 3)} ${d.getDate()} ${MESES_CORTOS[d.getMonth()]}${anio}`;
  }

  /** «lunes 5 de octubre», con el año si no es este. */
  diaLargo(d: Date | null): string {
    return d ? `${DIAS_SEMANA[d.getDay()]} ${this.bonita(this.iso(d))}` : "";
  }

  /**
   * Los festivos que se ven junto al calendario: cuatro mientras se eligen
   * las fechas y dos cuando ya está la cuenta verde, que ocupa el lugar de
   * los otros. Así el paso no crece al elegir el último día.
   */
  get festivosVisibles(): SolicitudVacacionesComponent["proximosFestivos"] {
    return this.proximosFestivos.slice(0, this.tramos.length ? 2 : 4);
  }

  /** «mañana», «en 47 días»: cuánto falta para una fecha, en días
   *  naturales. Para los próximos festivos. */
  private enCuanto(d: Date): string {
    const dias = Math.round((d.getTime() - this.hoy.getTime()) / 86400000);
    if (dias <= 0) {
      return "hoy";
    }
    return dias === 1 ? "mañana" : `en ${dias} días`;
  }

  /** Las hojitas de calendario de «Revisa»: mes, día de la semana y el
   *  año sólo si no es este. */
  mesDe(d: Date | null): string {
    return d ? MESES_CORTOS[d.getMonth()] : "";
  }

  semanaDe(d: Date | null): string {
    return d ? DIAS_SEMANA[d.getDay()] : "";
  }

  anioSiOtro(d: Date | null): string {
    return d && d.getFullYear() !== this.anio ? `${d.getFullYear()}` : "";
  }

  /**
   * «del lunes 5 al viernes 9 de octubre», «del lunes 28 de septiembre al
   * viernes 2 de octubre», o «el lunes 5 de octubre» si es uno solo. El
   * año va una vez, al final, salvo que las puntas sean de años distintos.
   */
  rangoLargo(desde: Date, hasta: Date): string {
    if (this.iso(desde) === this.iso(hasta)) {
      return `el ${this.diaLargo(desde)}`;
    }
    const mismoMes =
      desde.getMonth() === hasta.getMonth() && desde.getFullYear() === hasta.getFullYear();
    const inicio = mismoMes
      ? `${desde.getDate()}`
      : this.bonita(this.iso(desde), desde.getFullYear() !== hasta.getFullYear());
    return `del ${DIAS_SEMANA[desde.getDay()]} ${inicio} al ${this.diaLargo(hasta)}`;
  }

  /**
   * El día en que regresa: el hábil que sigue al último, sin fin de semana
   * ni festivo. El 1 de enero se salta aparte: es festivo por ley y el
   * servidor sólo manda los festivos hasta el año que entra, así que quien
   * termina el 31 de diciembre del año que entra «regresaría» el 1.
   */
  get textoRegreso(): string {
    // Con varios tramos no hay un solo regreso: se va y se vuelve varias veces.
    const h = this.unico ? this.unico.hasta : null;
    if (!h) {
      return "";
    }
    const d = new Date(h.getFullYear(), h.getMonth(), h.getDate() + 1);
    const descanso = (x: Date) =>
      !this.soloHabiles(x) || (x.getMonth() === 0 && x.getDate() === 1);
    // Con tope: nunca hay más de unos días seguidos sin trabajo.
    for (let i = 0; i < 15 && descanso(d); i++) {
      d.setDate(d.getDate() + 1);
    }
    return this.diaLargo(d);
  }

  /** Un solo día: «Revisa» enseña una hojita y no dos iguales. Se
   *  comparan las fechas y no los días hábiles: de viernes a lunes
   *  festivo también es un día hábil. */
  get unSoloDia(): boolean {
    const t = this.unico;
    return !!t && this.iso(t.desde) === this.iso(t.hasta);
  }

  /** «jue 15 oct», o «lun 19 – mar 20 oct» si el tramo es de varios días:
   *  cada ficha de «Días elegidos». */
  tramoCorto(t: Tramo): string {
    if (this.iso(t.desde) === this.iso(t.hasta)) {
      return this.diaCorto(t.desde);
    }
    const mismoMes = t.desde.getMonth() === t.hasta.getMonth() && t.desde.getFullYear() === t.hasta.getFullYear();
    const inicio = mismoMes
      ? `${DIAS_SEMANA[t.desde.getDay()].substring(0, 3)} ${t.desde.getDate()}`
      : this.diaCorto(t.desde);
    return `${inicio} – ${this.diaCorto(t.hasta)}`;
  }

  /** Si la solicitud es de las que se acaban de pedir (se resalta). */
  esNueva(v: Vacacion): boolean {
    return v.idVacacion != null && this.recienPedidas.includes(v.idVacacion);
  }

  /* ================= «Tus vacaciones»: filtro y páginas ================= */

  /*
   * 30/09/2026 (Norman: «no quiero scroll ni en vacaciones ni permisos,
   * paginado o diferente»): por páginas, tantas como caben en la columna
   * derecha junto al asistente sin que la pantalla ruede. Las tarjetas ya
   * no se despliegan: las acciones van en íconos en su renglón, como en
   * «Tus permisos», y el motivo o la nota, debajo.
   *
   * Y con filtro por estado (Norman: «filtro autorizada, no autorizada y
   * pendiente, bien elegante»): las tres familias que ya pinta la barra de
   * color de cada tarjeta, cada una con cuántas hay. Se prende y se apaga
   * tocándolo; apagado se ven todas. Sin un «Todas» aparte, los tres caben
   * en una línea junto al asistente.
   */

  readonly FILTROS_MIAS: { id: FiltroMias; texto: string; clase: string }[] = [
    { id: "PENDIENTE", texto: "En espera", clase: "asi-filtro--espera" },
    { id: "ACTIVO", texto: "Autorizadas", clase: "asi-filtro--ok" },
    { id: "RECHAZADA", texto: "No autorizadas", clase: "asi-filtro--no" },
  ];

  filtroMias: FiltroMias = "TODAS";
  paginaMias = 1;
  /** Cuántas por página: las que caben (ajustaPaginaMias). */
  porPaginaMias = 3;

  /** Recién enviada una, al recargar la lista se va a la página donde
   *  quedó (con varias en espera puede no ser la primera). */
  private irALaNueva = false;

  @ViewChild("listaMias") private listaMias?: ElementRef<HTMLElement>;

  /** La familia de una vacación, la misma que da el color de su barra
   *  (clase): lo que ni espera ni se negó cuenta como autorizada. */
  private familia(v: Vacacion): FiltroMias {
    if (v.estatus === "PENDIENTE") {
      return "PENDIENTE";
    }
    return v.estatus === "RECHAZADA" ? "RECHAZADA" : "ACTIVO";
  }

  cuantasMias(f: FiltroMias): number {
    return f === "TODAS"
      ? this.misSolicitudes.length
      : this.misSolicitudes.filter((v) => this.familia(v) === f).length;
  }

  /** Prende el filtro o, si ya estaba puesto, lo quita. */
  filtraMias(f: FiltroMias): void {
    this.filtroMias = this.filtroMias === f ? "TODAS" : f;
    this.paginaMias = 1;
  }

  /** Las de misPeriodos (las que esperan primero) con el filtro puesto. */
  get misPeriodosFiltrados(): Vacacion[] {
    const f = this.filtroMias;
    return f === "TODAS" ? this.misPeriodos : this.misPeriodos.filter((v) => this.familia(v) === f);
  }

  get totalPaginasMias(): number {
    return Math.max(1, Math.ceil(this.misPeriodosFiltrados.length / this.porPaginaMias));
  }

  /** La página acotada: al retirar la última de una página, la lista se
   *  recorre sola en vez de quedar en blanco. */
  get paginaMiasNorm(): number {
    return Math.min(Math.max(this.paginaMias, 1), this.totalPaginasMias);
  }

  get misPeriodosPagina(): Vacacion[] {
    const inicio = (this.paginaMiasNorm - 1) * this.porPaginaMias;
    return this.misPeriodosFiltrados.slice(inicio, inicio + this.porPaginaMias);
  }

  mueveMias(delta: number): void {
    this.paginaMias = Math.min(Math.max(this.paginaMiasNorm + delta, 1), this.totalPaginasMias);
  }

  /**
   * De cuántas en cuántas: las que caben entre el título de la lista y el
   * pie de la ventana (de 2 a 6). Se estima con el alto de una tarjeta
   * sencilla (92 px con su separación) y, ya pintadas, si las de la página
   * no caben (una con nota o motivo mide más), una menos hasta que quepan.
   */
  private ajustaPaginaMias(): void {
    const n = renglonesQueCaben(this.listaMias?.nativeElement, 92, { enColumna: 4 });
    if (n === null) {
      return;
    }
    if (n !== this.porPaginaMias) {
      this.porPaginaMias = n;
      this.cdr.detectChanges();
    }
    while (this.porPaginaMias > 2 && seDesborda(this.listaMias?.nativeElement)) {
      this.porPaginaMias--;
      this.cdr.detectChanges();
    }
  }

  /** Tras recargar: la página de la que se acaba de pedir. */
  private muestraLaNueva(): void {
    if (!this.irALaNueva) {
      return;
    }
    this.irALaNueva = false;
    const i = this.misPeriodosFiltrados.findIndex((v) => this.esNueva(v));
    this.paginaMias = i >= 0 ? Math.floor(i / this.porPaginaMias) + 1 : 1;
    this.cdr.detectChanges();
  }

  @HostListener("window:resize")
  alCambiarVentana(): void {
    this.ajustaPaginaMias();
    this.ajustaPaginaResolver();
  }

  /** De vuelta a la pestaña «Vacaciones»: si la ventana cambió mientras
   *  se veían los permisos, se vuelve a medir. */
  aVacaciones(): void {
    this.pestana = "vacaciones";
    this.cdr.detectChanges();
    this.ajustaPaginaMias();
  }

  /** El comentario, sin "NA", "N/A", "no aplica" ni compañía. */
  nota(v: Vacacion): string {
    return notaVisible(v.comentario);
  }

  trackVacacion(_: number, v: Vacacion): number {
    return v.idVacacion || 0;
  }

  /* ================= Utilidades ================= */

  /**
   * El periodo que empieza cuando termina `s`: el de inicio más cercano
   * desde su fin. No se da por hecho el orden en que vienen.
   */
  private siguienteDe(s: Saldo | null): Saldo | null {
    const fin = s && s.periodoFin;
    if (!fin) {
      return null;
    }
    let r: Saldo | null = null;
    for (const x of this.saldos) {
      if (x !== s && !!x.periodoInicio && x.periodoInicio >= fin &&
          (!r || x.periodoInicio < (r.periodoInicio || ""))) {
        r = x;
      }
    }
    return r;
  }

  /**
   * El último día que se puede pedir: el anterior al fin del último
   * periodo que existe (el fin es exclusivo: ya es el aniversario
   * siguiente), y a más tardar el 31 de diciembre del año que entra.
   *
   * Lo segundo es por los festivos: el servidor sólo manda los del año
   * pasado, el actual y el siguiente. Quien ya cumplió aniversario este año
   * tiene su periodo siguiente hasta dentro de dos años (alta del 20/09:
   * hasta el 19/09/2028), y en 2028 esta pantalla no sabría qué días son
   * festivos: diría 5 días donde el servidor cuenta 4 y «te quedarían» no
   * cuadraría con lo que se guarda. Nadie necesita pedir con más de un año
   * de anticipación. Si el servidor llegara a mandar más años de festivos,
   * este tope se puede quitar.
   */
  private ultimoDiaPedible(): Date {
    const tope = new Date(this.anio + 1, 11, 31);
    let fin = "";
    for (const s of this.saldos) {
      if (s.periodoFin && s.periodoFin > fin) {
        fin = s.periodoFin;
      }
    }
    if (!fin) {
      return tope;
    }
    const d = this.aFecha(fin);
    d.setDate(d.getDate() - 1);
    return d < tope ? d : tope;
  }

  /** dd/MM/aaaa, como las fechas de la hoja de RH. */
  corta(iso?: string | null): string {
    if (!iso) {
      return "";
    }
    const p = iso.substring(0, 10).split("-");
    return `${p[2]}/${p[1]}/${p[0]}`;
  }

  private finDeSemana(d: Date): boolean {
    const dia = d.getDay();
    return dia === 0 || dia === 6;
  }

  /**
   * Días hábiles entre dos fechas, contando ambos extremos y saltando
   * sábados y domingos. Es la misma cuenta que hace el servidor; aquí
   * sólo sirve para enseñarla mientras se captura.
   */
  private habilesEntre(desde: Date, hasta: Date): number {
    if (hasta < desde) {
      return 0;
    }
    let n = 0;
    const cursor = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate());
    const fin = new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate());
    while (cursor <= fin) {
      // Ni fin de semana ni festivo. Tiene que dar lo mismo que el
      // servidor: si aquí dijera 5 y allá se guardaran 4, el saldo que
      // ve el colaborador al capturar sería mentira.
      if (!this.finDeSemana(cursor) && !this.festivos.has(this.iso(cursor))) {
        n++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return n;
  }

  private aFecha(iso: string): Date {
    const p = iso.split("-");
    return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  }

  private iso(d: Date): string {
    const mes = `${d.getMonth() + 1}`.padStart(2, "0");
    const dia = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${mes}-${dia}`;
  }

  private aviso(texto: string, icono: "success" | "error" | "info"): void {
    swal.fire({
      text: texto,
      icon: icono,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#b71c1c",
    });
  }
}
